import {
  Component, ElementRef, HostListener, computed, effect, input, output, signal, viewChild
} from '@angular/core';
import {
  RoleGrant, RoleGrantInvitation, grantSummaryLabel, roleLabel, scopeDescription
} from './grant.models';

/**
 * What the dialog is confirming — an invitation (which carries a whole role set and, once redeemed, the
 * grants it produced) or a single already-issued grant (one role, one holder).
 *
 * `superseded` (#759) is the set of still-active grants the one being revoked **covers** — the ones that
 * would survive a revoke of it alone. Optional and absent on every pre-#759 call site, which is what keeps
 * the plain single-grant confirmation unchanged.
 */
export type RevokeTarget =
  | {kind: 'invitation'; invitation: RoleGrantInvitation}
  | {kind: 'grant'; grant: RoleGrant; superseded?: readonly RoleGrant[]};

/**
 * Confirms a revoke — of a whole invitation or of one already-granted role — in a modal, not inline in the
 * table row it was clicked from.
 *
 * ### Why a modal, not the row (#279 follow-up)
 *
 * The inline version swapped a one-line "Widerrufen" link for a full sentence plus two buttons **inside the
 * same table cell**: the row grew, the column widths recomputed, and every row below it shifted under the
 * cursor. On a dense table that is how a product owner confirmed the wrong invitation during live testing —
 * revoking is not a reversible action (it withdraws live access and cascades to every grant an invitation
 * produced), so the surface asking for confirmation must not itself be the thing that causes a mis-click.
 * Rendering the confirmation OUTSIDE the row removes the layout shift entirely: nothing about the row's size
 * or position changes while this is open.
 *
 * ### Naming what is at stake, not just that something will happen
 *
 * `roles()`/`scope()`/`holder()` exist so the copy names the actual scope, roles and (for a grant) the person
 * — the inline version's "Auch bereits erteilte Rollen werden entzogen." is kept verbatim (the manual test
 * plan checks for that exact sentence), but it is the FLOOR here, not the whole message.
 *
 * ### Dismissible by backdrop and Escape — unlike {@link InviteCodeDialogComponent}
 *
 * That dialog refuses every accidental exit because its secret is unrecoverable once closed: there is no
 * "show it again". Cancelling a revoke has no such cost — the row is untouched either way — so a stray click
 * outside or an `Escape` press is treated as an ordinary **cancel**, the safe direction, rather than being
 * blocked like the code dialog blocks it. That asymmetry between the two dialogs is deliberate, not an
 * oversight: don't "fix" this one to match the other.
 *
 * ### The superseded-grant branch (#759) — a toggle, not a second button
 *
 * A grant whose role cascades (`CompanyOwner`, company-scoped `ShiftLead`) covers narrower shard-scoped
 * grants the same person holds. Revoking it alone leaves those behind, so "I removed their CompanyOwner" was
 * never "I removed their access" — and nothing said so. When {@link RevokeTarget}'s `superseded` list is
 * non-empty this dialog therefore **names every grant that would survive** and offers one switch,
 * „Auch abgedeckte Rollen entziehen", directly beneath that list.
 *
 * The list is what makes the wide outcome safe: nothing is removed that is not written on the screen the
 * operator is looking at. Three rules hold the risk down:
 *
 * 1. **„Abbrechen" still holds focus**, so a stray `Enter` confirms nothing, whatever the switch says.
 * 2. **The switch is off by default and resets on every target**, so the dialog's default action is always
 *    the narrow one, and a switch left on for one row cannot carry into the next.
 * 3. **With nothing covered the dialog is unchanged** — same two buttons, same „Ja, entziehen" label, same
 *    footer markup, no switch at all. That is the overwhelmingly common case and it was already right.
 *
 * It was briefly a third button („Nur diese entziehen" beside „Alle N entziehen"). That is recorded here
 * because the reason it went is not obvious: three buttons did not fit the `sm:max-w-lg` panel, and a
 * `justify-end` row that overflows pushes its FIRST item out of view rather than its last, so „Abbrechen"
 * silently left the dialog. Widening the panel or shortening the labels would have hidden that; the real
 * problem was asking the reader to pick between two similar labels to express one yes/no choice.
 */
@Component({
  selector: 'company-revoke-confirm-dialog',
  standalone: true,
  template: `
    <!-- Backdrop click cancels (see the class doc: safe here, unlike the code dialog). -->
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <div class="w-full max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg
                  sm:rounded-2xl"
           role="alertdialog" aria-modal="true" aria-labelledby="revoke-confirm-heading"
           aria-describedby="revoke-confirm-body" (click)="$event.stopPropagation()">
        <div class="space-y-4 p-4 sm:p-6">

          <h2 id="revoke-confirm-heading" class="text-xl font-bold tracking-tight text-gray-900">
            {{ heading() }}
          </h2>

          <div id="revoke-confirm-body" class="space-y-3 text-sm text-gray-700">
            @if (scope(); as s) {
              <p>{{ s }}</p>
            }
            @if (holder(); as h) {
              <p>Person: <span class="font-semibold text-gray-900">{{ h }}</span></p>
            }
            @if (roles().length) {
              <div class="flex flex-wrap gap-1">
                @for (role of roles(); track role) {
                  <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs
                               font-semibold text-gray-600">{{ label(role) }}</span>
                }
              </div>
            }
            @if (isInvitation()) {
              <!-- Kept verbatim — the manual test plan checks for this exact sentence. -->
              <p class="rounded-2xl bg-red-50 px-4 py-3 font-semibold text-red-700" role="alert">
                Auch bereits erteilte Rollen werden entzogen.
              </p>
            }

            <!-- #759: what stays behind. Neutral, not a red warning — these roles are not a danger, they are
                 the thing the operator is about to discover they did NOT remove. -->
            @if (superseded().length) {
              <div class="rounded-2xl bg-gray-50 px-4 py-3">
                <p class="font-semibold text-gray-900">Diese Person behält danach:</p>
                <ul class="mt-1 space-y-0.5">
                  @for (kept of superseded(); track kept.id) {
                    <li class="text-gray-700">{{ summary(kept) }}</li>
                  }
                </ul>
                <!-- The choice is a TOGGLE, not a third button: the footer's two buttons stay the two every
                     other dialog on this screen has, and the question "how far does this reach?" is answered
                     where the roles it reaches are listed, rather than in a button label the operator reads
                     last. The house switch pattern, same markup as the invitation form's binding toggle —
                     the label wraps only the control, so the words need the explicit aria association or it
                     announces as an unnamed checkbox. -->
                <div class="mt-3 flex items-start justify-between gap-3 rounded-xl border border-gray-200
                            bg-white p-3">
                  <span class="min-w-0">
                    <span id="revoke-all-label" class="block text-sm font-medium text-gray-900">
                      Auch abgedeckte Rollen entziehen
                    </span>
                    <span id="revoke-all-hint" class="block text-xs text-gray-700">
                      Eingeschaltet: die oben genannten Rollen werden zusammen mit der gewählten entzogen.
                      Ausgeschaltet: sie bleiben bestehen.
                    </span>
                  </span>
                  <label class="relative inline-flex shrink-0 cursor-pointer items-center">
                    <input id="revoke-all" type="checkbox" class="peer sr-only" data-test="revoke-all-toggle"
                           [checked]="alsoCovered()" [disabled]="busy()"
                           aria-labelledby="revoke-all-label" aria-describedby="revoke-all-hint"
                           (change)="alsoCovered.set(checked($event))">
                    <span class="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-0.5
                                 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white
                                 after:transition-all peer-checked:bg-theme-primary-600
                                 peer-checked:after:translate-x-5 peer-disabled:cursor-not-allowed
                                 peer-disabled:opacity-50"></span>
                  </label>
                </div>
              </div>
            }
          </div>

          <div [class]="footerClass()">
            <!-- Cancel is the low-emphasis control and holds focus by default: the safe answer to a
                 destructive prompt should be the one a stray Enter press lands on. The switch above does not
                 change that — it decides how far a confirm reaches, never whether one happens. -->
            <button type="button" #cancelButton (click)="cancel()"
                    class="inline-flex items-center justify-center whitespace-nowrap rounded-2xl border
                           border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 shadow-sm
                           transition-all duration-200 hover:border-gray-300 hover:bg-gray-50
                           active:scale-95">
              Abbrechen
            </button>
            <button type="button" (click)="confirm()" [disabled]="busy()"
                    class="inline-flex items-center justify-center whitespace-nowrap rounded-2xl bg-red-600
                           px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-red-100
                           transition-all duration-200 hover:bg-red-700 hover:shadow-xl active:scale-95
                           disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
              {{ confirmLabel() }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class RevokeConfirmDialogComponent {
  /** What is being revoked — set once; the parent mounts a fresh instance per confirmation. */
  readonly target = input.required<RevokeTarget>();

  /** Disables the confirm button while the revoke call is in flight — mirrors the row's own `busy` state. */
  readonly busy = input(false);

  /** The revoke was confirmed — the caller makes the call and closes the dialog on success. */
  readonly confirmed = output<void>();

  /**
   * „Alle N entziehen" was confirmed (#759): revoke the target **and** every grant it covers, in one action.
   *
   * A separate output rather than a payload on {@link confirmed}, so a caller that never passes `superseded`
   * cannot accidentally widen a revoke — the wider intent has to be subscribed to deliberately.
   */
  readonly confirmedAll = output<void>();

  /** Cancel button, backdrop click, or Escape — all the same outcome: nothing changes. */
  readonly cancelled = output<void>();

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  protected readonly label = roleLabel;
  protected readonly summary = grantSummaryLabel;

  protected readonly isInvitation = computed(() => this.target().kind === 'invitation');

  /**
   * The still-active grants the target covers (#759) — empty for an invitation and for every grant that
   * covers nothing, which is what keeps the two-button dialog exactly as it was.
   */
  protected readonly superseded = computed<readonly RoleGrant[]>(() => {
    const t = this.target();
    return t.kind === 'grant' ? (t.superseded ?? []) : [];
  });

  protected readonly heading = computed(() =>
    this.isInvitation() ? 'Einladung widerrufen?' : 'Rolle entziehen?');

  /**
   * The confirm button's label — „Ja, widerrufen" for an invitation, „Ja, entziehen" for a grant.
   *
   * <b>It does not vary with coverage.</b> It briefly read „Nur diese entziehen" while a second, wider button
   * sat beside it and „Ja" no longer said which of the two was meant. With the choice in the switch there is
   * only one confirm, so „Ja" is unambiguous again — and keeping the label fixed is what lets the covered and
   * uncovered dialogs be the same footer rather than two that happen to look alike.
   */
  protected readonly confirmLabel = computed(() =>
    this.isInvitation() ? 'Ja, widerrufen' : 'Ja, entziehen');

  /**
   * Whether the revoke also takes the covered roles with it (#759) — the toggle beside the keep list.
   *
   * <b>A toggle, not a third button.</b> The footer is the same two buttons every other dialog on this
   * screen has, and the question it used to ask in a button label („Nur diese entziehen" beside „Alle N
   * entziehen") is asked where its answer is visible: directly under the list of roles it decides the fate
   * of. Three buttons also did not fit — at `sm:max-w-lg` the set overflowed and `justify-end` pushed
   * „Abbrechen" out of the panel — but that was the symptom. The reason is that a reader should not have to
   * compare two button labels to work out which one matches what they just read.
   *
   * <b>Off by default</b>, so the dialog's default action stays the narrow one it has always been. Reset
   * whenever the target changes, in the same effect that restores focus, so a toggle left on for one row can
   * never carry into the next.
   */
  protected readonly alsoCovered = signal(false);

  /**
   * The footer's classes — now identical in both shapes, because the footer itself is.
   *
   * It used to branch: three buttons stacked below `sm` and wrapped above it. With the wide action moved to
   * a toggle there is one layout again, and it is the original single-line string, byte-for-byte. A spec
   * pins that, because "the common case is untouched" is the property that made this change safe to make.
   */
  protected readonly footerClass = computed(() => 'flex justify-end gap-3 border-t border-gray-100 pt-4');

  /** The tenant + scope line — only an invitation carries enough to describe one (a grant already has a row). */
  protected readonly scope = computed<string | null>(() => {
    const t = this.target();
    return t.kind === 'invitation' ? scopeDescription(t.invitation) : null;
  });

  /** The grant's holder — the one thing a grant names that an invitation (not yet redeemed by anybody) can't. */
  protected readonly holder = computed<string | null>(() => {
    const t = this.target();
    return t.kind === 'grant' ? (t.grant.identityName || 'Unbekanntes Konto') : null;
  });

  protected readonly roles = computed<string[]>(() => {
    const t = this.target();
    return t.kind === 'invitation' ? t.invitation.roles : [t.grant.role];
  });

  constructor() {
    // Put the caret on Cancel as soon as the dialog appears — the safe default for a destructive prompt.
    // The toggle is cleared in the SAME effect, keyed on the target: the host reuses one dialog instance for
    // every row, so a toggle left on for the row before would otherwise be armed before the operator has
    // read anything. Read `target()` explicitly so the effect re-runs per target rather than only on first
    // render.
    effect(() => {
      this.target();
      this.alsoCovered.set(false);
      const button = this.cancelButton()?.nativeElement;
      if (button) button.focus();
    });
  }

  /**
   * The one confirm. Which revoke it asks for is decided by {@link alsoCovered}, not by which button was
   * pressed — and the wide outcome is guarded on `superseded` as well, so a toggle that somehow survived a
   * target change can never widen a revoke that covers nothing.
   */
  protected confirm(): void {
    if (this.busy()) return;
    if (this.alsoCovered() && this.superseded().length) this.confirmedAll.emit();
    else this.confirmed.emit();
  }

  /** Reads a checkbox's new state out of its change event. */
  protected checked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
